import React, { useState, useEffect } from 'react';
import { config } from '../config';
import { Link, useNavigate } from 'react-router-dom';
import { useNotification } from '../components/Notification';
import './ApiDocumentation.css';

function ApiDocumentation() {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const baseUrl = config.API_URL || 'https://api.garagedesk.net/api';
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [apiToken, setApiToken] = useState('');
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [tokens, setTokens] = useState([]);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [requestBody, setRequestBody] = useState('{}');
  const [requestParams, setRequestParams] = useState({});

  const loadApiTokens = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await fetch(`${baseUrl}/api-tokens`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setTokens(data || []);
        // Use session token for testing (we can't retrieve the actual API token)
        if (token) {
          setApiToken(token);
        }
      }
    } catch (error) {
      console.error('Error loading API tokens:', error);
    }
  };

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    if (token && user) {
      try {
        const parsedUser = JSON.parse(user);
        if (parsedUser.role === 'tenantAdmin' || parsedUser.role === 'admin') {
          setIsAuthenticated(true);
          setApiToken(token);
          loadApiTokens();
        }
      } catch (e) {
        console.error('Error parsing user:', e);
      }
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${baseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(loginData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        showNotification(errorData.error || 'Anmeldung fehlgeschlagen', 'error');
        setLoading(false);
        return;
      }

      const data = await response.json();
      if (data.token && data.user) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setIsAuthenticated(true);
        setApiToken(data.token);
        await loadApiTokens();
        showNotification('Erfolgreich angemeldet', 'success');
      }
    } catch (error) {
      showNotification('Fehler bei der Anmeldung', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateToken = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseUrl}/api-tokens`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: 'API Documentation Token',
          expiryDays: 90
        })
      });

      if (response.ok) {
        const data = await response.json();
        setApiToken(data.token);
        await loadApiTokens();
        showNotification('API-Token erfolgreich erstellt', 'success');
      } else {
        const errorData = await response.json().catch(() => ({}));
        showNotification(errorData.error || 'Fehler beim Erstellen des Tokens', 'error');
      }
    } catch (error) {
      showNotification('Fehler beim Erstellen des Tokens', 'error');
    }
  };

  const testEndpoint = async (endpoint) => {
    if (!apiToken) {
      showNotification('Bitte erstellen Sie zuerst einen API-Token', 'error');
      return;
    }

    setTestLoading(true);
    setTestResult(null);
    
    let url = '';
    try {
      const method = endpoint.method || 'GET';
      url = `${baseUrl}${endpoint.path}`;
      
      // Replace path parameters
      Object.keys(requestParams).forEach(key => {
        if (url.includes(`:${key}`)) {
          url = url.replace(`:${key}`, requestParams[key]);
        }
      });

      // Add query parameters
      const queryParams = Object.keys(requestParams)
        .filter(key => !url.includes(`:${key}`))
        .map(key => `${key}=${encodeURIComponent(requestParams[key])}`)
        .join('&');
      
      if (queryParams) {
        url += (url.includes('?') ? '&' : '?') + queryParams;
      }

      const options = {
        method,
        headers: {
          'Authorization': `Bearer ${apiToken}`,
          'Content-Type': 'application/json'
        }
      };

      if (method !== 'GET' && method !== 'DELETE' && requestBody) {
        try {
          options.body = JSON.stringify(JSON.parse(requestBody));
        } catch (e) {
          showNotification('Ungültiges JSON im Request Body', 'error');
          setTestLoading(false);
          return;
        }
      }

      const startTime = Date.now();
      const response = await fetch(url, options);
      const endTime = Date.now();
      const duration = endTime - startTime;

      let responseData;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      setTestResult({
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        data: responseData,
        duration,
        url
      });
    } catch (error) {
      setTestResult({
        error: error.message,
        url: url || 'Unknown'
      });
    } finally {
      setTestLoading(false);
    }
  };

  const endpoints = [
    {
      category: 'Kunden',
      endpoints: [
        { method: 'GET', path: '/customers', description: 'Alle Kunden abrufen', needsAuth: true },
        { method: 'GET', path: '/customers/:id', description: 'Einzelnen Kunden abrufen', needsAuth: true, params: ['id'] },
        { method: 'POST', path: '/customers', description: 'Neuen Kunden erstellen', needsAuth: true, hasBody: true },
        { method: 'PUT', path: '/customers/:id', description: 'Kunden aktualisieren', needsAuth: true, params: ['id'], hasBody: true },
        { method: 'DELETE', path: '/customers/:id', description: 'Kunden löschen', needsAuth: true, params: ['id'] }
      ]
    },
    {
      category: 'Fahrzeuge',
      endpoints: [
        { method: 'GET', path: '/cars', description: 'Alle Fahrzeuge abrufen', needsAuth: true },
        { method: 'GET', path: '/cars?customerId=:customerId', description: 'Fahrzeuge eines Kunden', needsAuth: true, params: ['customerId'] },
        { method: 'GET', path: '/cars/:id', description: 'Einzelnes Fahrzeug abrufen', needsAuth: true, params: ['id'] },
        { method: 'POST', path: '/cars', description: 'Neues Fahrzeug erstellen', needsAuth: true, hasBody: true },
        { method: 'PUT', path: '/cars/:id', description: 'Fahrzeug aktualisieren', needsAuth: true, params: ['id'], hasBody: true },
        { method: 'DELETE', path: '/cars/:id', description: 'Fahrzeug löschen', needsAuth: true, params: ['id'] }
      ]
    },
    {
      category: 'Reparaturen',
      endpoints: [
        { method: 'GET', path: '/repairs', description: 'Alle Reparaturen abrufen', needsAuth: true },
        { method: 'GET', path: '/repairs?carId=:carId', description: 'Reparaturen eines Fahrzeugs', needsAuth: true, params: ['carId'] },
        { method: 'GET', path: '/repairs/:id', description: 'Einzelne Reparatur abrufen', needsAuth: true, params: ['id'] },
        { method: 'POST', path: '/repairs', description: 'Neue Reparatur erstellen', needsAuth: true, hasBody: true },
        { method: 'PUT', path: '/repairs/:id', description: 'Reparatur aktualisieren', needsAuth: true, params: ['id'], hasBody: true },
        { method: 'DELETE', path: '/repairs/:id', description: 'Reparatur löschen', needsAuth: true, params: ['id'] }
      ]
    },
    {
      category: 'Rechnungen',
      endpoints: [
        { method: 'GET', path: '/invoices', description: 'Alle Rechnungen abrufen', needsAuth: true },
        { method: 'GET', path: '/invoices/:id', description: 'Einzelne Rechnung abrufen', needsAuth: true, params: ['id'] },
        { method: 'GET', path: '/invoices/:id/pdf', description: 'Rechnung als PDF abrufen', needsAuth: true, params: ['id'] },
        { method: 'POST', path: '/invoices/from-repair/:repairId', description: 'Rechnung aus Reparatur erstellen', needsAuth: true, params: ['repairId'] },
        { method: 'PUT', path: '/invoices/:id', description: 'Rechnung aktualisieren', needsAuth: true, params: ['id'], hasBody: true },
        { method: 'DELETE', path: '/invoices/:id', description: 'Rechnung löschen', needsAuth: true, params: ['id'] }
      ]
    },
    {
      category: 'API-Tokens',
      endpoints: [
        { method: 'GET', path: '/api-tokens', description: 'Alle API-Tokens abrufen', needsAuth: true },
        { method: 'POST', path: '/api-tokens', description: 'Neuen API-Token erstellen', needsAuth: true, hasBody: true },
        { method: 'DELETE', path: '/api-tokens/:id', description: 'API-Token löschen', needsAuth: true, params: ['id'] }
      ]
    }
  ];

  if (!isAuthenticated) {
    return (
      <div className="api-documentation">
        <div className="api-documentation-container">
          <button onClick={() => navigate(-1)} className="btn-back">
            ← Zurück
          </button>
          <h1>GarageDesk API Dokumentation</h1>
          <p className="api-documentation-intro">
            Bitte melden Sie sich als TenantAdmin oder Admin an, um die interaktive API-Dokumentation zu nutzen.
          </p>
          
          <div className="login-section">
            <h2>Anmeldung</h2>
            <form onSubmit={handleLogin} className="api-login-form">
              <div className="form-group">
                <label htmlFor="username">Benutzername</label>
                <input
                  type="text"
                  id="username"
                  value={loginData.username}
                  onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Passwort</label>
                <input
                  type="password"
                  id="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                />
              </div>
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Anmelden...' : 'Anmelden'}
              </button>
            </form>
            <p className="login-hint">
              Sie können sich auch über die normale <Link to="/login">Login-Seite</Link> anmelden.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="api-documentation">
      <div className="api-documentation-container">
        <button onClick={() => navigate(-1)} className="btn-back">
          ← Zurück
        </button>
        <h1>GarageDesk API Dokumentation</h1>
        <p className="api-documentation-intro">
          Interaktive API-Dokumentation - Testen Sie die API-Endpunkte direkt im Browser.
        </p>

        <div className="api-token-section">
          <h2>API-Token</h2>
          {apiToken ? (
            <div className="token-display">
              <label>Ihr API-Token:</label>
              <div className="token-input-group">
                <input
                  type="text"
                  value={apiToken}
                  readOnly
                  className="token-input"
                />
                <button
                  className="btn-secondary"
                  onClick={() => {
                    navigator.clipboard.writeText(apiToken);
                    showNotification('Token kopiert', 'success');
                  }}
                >
                  Kopieren
                </button>
              </div>
              <button className="btn-secondary" onClick={handleCreateToken}>
                Neuen Token erstellen
              </button>
            </div>
          ) : (
            <div>
              <p>Sie haben noch keinen API-Token. Erstellen Sie einen, um die API zu testen.</p>
              <button className="btn-primary" onClick={handleCreateToken}>
                API-Token erstellen
              </button>
            </div>
          )}
        </div>

        <div className="endpoints-section">
          <h2>API-Endpunkte</h2>
          {endpoints.map(category => (
            <div key={category.category} className="endpoint-category">
              <h3>{category.category}</h3>
              {category.endpoints.map((endpoint, idx) => (
                <div key={idx} className="endpoint-item">
                  <div className="endpoint-header">
                    <span className={`method-badge method-${endpoint.method.toLowerCase()}`}>
                      {endpoint.method}
                    </span>
                    <code className="endpoint-path">{endpoint.path}</code>
                    <span className="endpoint-description">{endpoint.description}</span>
                    <button
                      className="btn-small btn-primary"
                      onClick={() => {
                        setSelectedEndpoint(endpoint);
                        setRequestParams({});
                        setRequestBody('{}');
                        setTestResult(null);
                      }}
                    >
                      Testen
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {selectedEndpoint && (
          <div className="test-modal">
            <div className="test-modal-content">
              <div className="test-modal-header">
                <h2>
                  <span className={`method-badge method-${selectedEndpoint.method.toLowerCase()}`}>
                    {selectedEndpoint.method}
                  </span>
                  {selectedEndpoint.path}
                </h2>
                <button className="close-btn" onClick={() => setSelectedEndpoint(null)}>×</button>
              </div>

              <div className="test-form">
                {selectedEndpoint.params && selectedEndpoint.params.length > 0 && (
                  <div className="form-section">
                    <h3>Pfad-Parameter</h3>
                    {selectedEndpoint.params.map(param => (
                      <div key={param} className="form-group">
                        <label>{param}</label>
                        <input
                          type="text"
                          value={requestParams[param] || ''}
                          onChange={(e) => setRequestParams({ ...requestParams, [param]: e.target.value })}
                          placeholder={`Wert für ${param}`}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {selectedEndpoint.hasBody && (
                  <div className="form-section">
                    <h3>Request Body (JSON)</h3>
                    <textarea
                      value={requestBody}
                      onChange={(e) => setRequestBody(e.target.value)}
                      rows={10}
                      className="json-textarea"
                      placeholder='{"key": "value"}'
                    />
                  </div>
                )}

                <div className="test-actions">
                  <button
                    className="btn-primary"
                    onClick={() => testEndpoint(selectedEndpoint)}
                    disabled={testLoading}
                  >
                    {testLoading ? 'Sende Anfrage...' : 'Anfrage senden'}
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => setSelectedEndpoint(null)}
                  >
                    Schließen
                  </button>
                </div>
              </div>

              {testResult && (
                <div className="test-result">
                  <h3>Antwort</h3>
                  <div className="result-status">
                    <span className={`status-badge status-${testResult.status >= 200 && testResult.status < 300 ? 'success' : 'error'}`}>
                      {testResult.status} {testResult.statusText}
                    </span>
                    {testResult.duration && <span className="duration">({testResult.duration}ms)</span>}
                  </div>
                  {testResult.url && (
                    <div className="result-url">
                      <strong>URL:</strong> <code>{testResult.url}</code>
                    </div>
                  )}
                  <div className="result-data">
                    <pre>{JSON.stringify(testResult.data || testResult.error, null, 2)}</pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ApiDocumentation;
